// ScorePickerSheet: Responsive score selection popup.
// Mobile (< 768px): Bottom sheet sliding up from bottom of screen.
// Desktop (>= 768px): Centered modal with backdrop blur.
// Renders via React portal into document.body to overlay the bracket correctly.
//
// For score-eligible rounds (R1, Finals): shows all 8 score options grouped by team.
// Picking a score sets both winner + score in one action.
// For non-score rounds (R2, CF): shows two team buttons to pick winner.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, HelpCircle } from 'lucide-react';
import TeamLogo from '@/components/TeamLogo';
import { BracketPick } from '@/types/bracket';
import { isPlaceholderTeam } from '@/lib/placeholder-utils';

interface SeriesOddsRow {
  id: string;
  series_id: string | null;
  team: string;
  outcome: string;
  team_wins: number;
  opponent_wins: number;
  american_odds: number;
  decimal_odds: number | null;
  multiplier: number | null;
}

interface ScorePickerSheetProps {
  isOpen: boolean;
  onClose: () => void;
  slotId: string;
  round: string;
  teamHome: string | null;
  teamAway: string | null;
  pick?: BracketPick;
  odds: SeriesOddsRow[];
  isLocked: boolean;
  isViewingOther: boolean;
  hasDownstreamPicks: boolean;
  onPickScore: (slotId: string, score: string, americanOdds: number | null, winner: string) => void;
  onClearPick: (slotId: string) => void;
  onPickWinnerOnly?: (slotId: string, winner: string) => void;
}

const ROUND_LABELS: Record<string, string> = {
  round1: 'סיבוב 1',
  first_round: 'סיבוב 1',
  finals: 'הגמר',
  west_semis: 'סיבוב 2 – מערב',
  east_semis: 'סיבוב 2 – מזרח',
  west_finals: 'גמר מערבי',
  east_finals: 'גמר מזרחי',
};


const ScorePickerSheet = ({
  isOpen,
  onClose,
  slotId,
  round,
  teamHome,
  teamAway,
  pick,
  odds,
  isLocked,
  isViewingOther,
  hasDownstreamPicks,
  onPickScore,
  onClearPick,
  onPickWinnerOnly,
}: ScorePickerSheetProps) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lock body scroll when sheet is open.
  // Only set overflow:hidden — no position:fixed trick, which caused black screen
  // when restoration failed on Android.
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const disabled = isLocked || isViewingOther;
  const roundLabel = ROUND_LABELS[round] ?? round;
  const isPlaceholder = isPlaceholderTeam(teamHome) || isPlaceholderTeam(teamAway);

  const handleScoreClick = (row: SeriesOddsRow) => {
    if (disabled) return;
    const score = `${row.team_wins}-${row.opponent_wins}`;
    onPickScore(slotId, score, row.american_odds || null, row.team);
    // parent onPickScore wrapper also calls setActiveScoreSlot(null) which closes
  };

  const handleClearPick = () => {
    if (disabled || !pick) return;
    if (hasDownstreamPicks) {
      if (!window.confirm('ניקוי הבחירה ימחק גם את הניחושים של הסיבובים הבאים. להמשיך?')) return;
    }
    onClearPick(slotId);
    onClose();
  };

  // Safety: if open but no slot data, close to prevent black screen
  useEffect(() => {
    if (isOpen && !slotId && !teamHome && !teamAway) {
      onClose();
    }
  }, [isOpen, slotId, teamHome, teamAway, onClose]);

  const handlePickWinnerOnly = onPickWinnerOnly
    ? (winner: string) => {
        onPickWinnerOnly(slotId, winner);
        onClose();
      }
    : undefined;

  const sheetContentProps = {
    roundLabel,
    teamHome,
    teamAway,
    pick,
    odds,
    disabled,
    isPlaceholder,
    onClose,
    onScoreClick: handleScoreClick,
    onClearPick: handleClearPick,
    onPickWinnerOnly: handlePickWinnerOnly,
  };

  // Each motion component is a direct child of AnimatePresence (no fragment)
  // to ensure Framer Motion properly tracks enter/exit animations on all devices
  const content = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
      )}

      {isOpen && isMobile && (
        <motion.div
          key="sheet"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-[101] bg-card border border-border rounded-t-2xl overflow-y-auto"
          style={{ maxHeight: '70vh' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-border" />
          </div>
          <SheetContent {...sheetContentProps} />
        </motion.div>
      )}

      {isOpen && !isMobile && (
        <motion.div
          key="modal"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[101] flex items-center justify-center pointer-events-none"
        >
          <div
            className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm pointer-events-auto overflow-y-auto"
            style={{ maxHeight: '80vh' }}
            onClick={e => e.stopPropagation()}
          >
            <SheetContent {...sheetContentProps} />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(content, document.body);
};

// ── Inner content ───────────────────────────────────────────────────────────

// Standard score options: 4-0, 4-1, 4-2, 4-3
const SCORE_OPTIONS: Array<{ wins: number; losses: number }> = [
  { wins: 4, losses: 3 },
  { wins: 4, losses: 2 },
  { wins: 4, losses: 1 },
  { wins: 4, losses: 0 },
];

interface SheetContentProps {
  roundLabel: string;
  teamHome: string | null;
  teamAway: string | null;
  pick?: BracketPick;
  odds: SeriesOddsRow[];
  disabled: boolean;
  isPlaceholder: boolean;
  onClose: () => void;
  onScoreClick: (row: SeriesOddsRow) => void;
  onClearPick: () => void;
  onPickWinnerOnly?: (winner: string) => void;
}

const SheetContent = ({
  roundLabel,
  teamHome,
  teamAway,
  pick,
  odds,
  disabled,
  isPlaceholder,
  onClose,
  onScoreClick,
  onClearPick,
  onPickWinnerOnly,
}: SheetContentProps) => {
  const teams = [teamHome, teamAway].filter((t): t is string => t != null);

  // Fallback if no teams
  if (teams.length === 0) {
    return (
      <div className="px-4 pb-6" dir="rtl">
        <div className="flex items-center justify-between py-3 mb-4">
          <h2 className="text-base font-bold tracking-tight">{roundLabel}</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-secondary/60 transition-colors"
            aria-label="סגור"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground text-center py-6">ממתין לנתונים</p>
      </div>
    );
  }

  // ── Placeholder mode: winner-only, no exact score ────────────────────────
  if (isPlaceholder) {
    return (
      <div className="px-4 pb-6" dir="rtl">
        {/* Header */}
        <div className="flex items-center justify-between py-3 mb-2">
          <h2 className="text-base font-bold tracking-tight">{roundLabel}</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-secondary/60 transition-colors"
            aria-label="סגור"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Explanation */}
        <p className="text-muted-foreground text-sm text-center py-4">
          לא ניתן לבחור תוצאה מדויקת — ממתין ל-Play-In
        </p>

        {/* Winner-only buttons, one per team */}
        <div className="flex flex-col gap-3 mt-1">
          {teams.map(team => {
            const isTeamPlaceholder = isPlaceholderTeam(team);
            const isSelected = pick?.winner === team;
            return (
              <button
                key={team}
                disabled={disabled}
                onClick={() => !disabled && onPickWinnerOnly?.(team)}
                className={`
                  flex items-center gap-3 w-full rounded-xl px-4 py-3 border transition-all text-right
                  ${isSelected
                    ? 'border-accent bg-accent/15'
                    : 'border-border hover:border-accent/40 hover:bg-secondary/40'}
                  ${disabled ? 'cursor-default opacity-75' : 'cursor-pointer'}
                `}
              >
                {isTeamPlaceholder
                  ? <HelpCircle size={28} className="shrink-0 text-muted-foreground/40" />
                  : <TeamLogo teamName={team} size={28} className="shrink-0" />
                }
                <span className={`text-sm font-medium flex-1 ${isTeamPlaceholder ? 'text-muted-foreground/65' : 'text-foreground'}`}>
                  {team}
                </span>
              </button>
            );
          })}
        </div>

        {/* Clear pick button */}
        {pick && !disabled && (
          <div className="flex justify-center pt-4 pb-1">
            <button onClick={onClearPick} className="text-sm" style={{ color: '#999' }}>
              נקה בחירה
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Normal mode: full score grid ─────────────────────────────────────────

  // Build score rows per team — use real odds if available, otherwise synthetic
  const getTeamRows = (team: string): SeriesOddsRow[] => {
    const realOdds = odds.filter(o => o.team === team);
    if (realOdds.length > 0) return realOdds;
    // Generate synthetic score options (no odds)
    return SCORE_OPTIONS.map(({ wins, losses }) => ({
      id: `${team}_${wins}-${losses}`,
      series_id: null,
      team,
      outcome: `${wins}-${losses}`,
      team_wins: wins,
      opponent_wins: losses,
      american_odds: 0,
      decimal_odds: null,
      multiplier: null,
    }));
  };

  return (
    <div className="px-4 pb-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between py-3 mb-2">
        <h2 className="text-base font-bold tracking-tight">{roundLabel}</h2>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-secondary/60 transition-colors"
          aria-label="סגור"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Score buttons grouped by team */}
      {teams.map(team => {
        const teamRows = getTeamRows(team);
        return (
          <div key={team} className="mb-5">
            {/* Team header */}
            <div className="flex items-center gap-2 mb-2.5">
              <TeamLogo teamName={team} size={32} className="shrink-0" />
              <span className="text-sm font-bold">{team}</span>
            </div>
            {/* Score buttons in a row */}
            <div className="grid grid-cols-4 gap-2">
              {teamRows.map(row => {
                const score = `${row.team_wins}-${row.opponent_wins}`;
                const isSelected = pick?.winner === team && pick?.predicted_score === score;
                return (
                  <button
                    key={row.id}
                    disabled={disabled}
                    onClick={() => onScoreClick(row)}
                    className={`
                      flex flex-col items-center gap-0.5 rounded-xl px-2 py-3 border transition-all
                      ${isSelected
                        ? 'border-accent bg-accent/15'
                        : 'border-border hover:border-accent/40 hover:bg-secondary/40'}
                      ${disabled ? 'cursor-default opacity-75' : 'cursor-pointer'}
                    `}
                  >
                    <span className="text-sm font-bold font-mono text-foreground">
                      {score}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Clear pick button */}
      {pick && !disabled && (
        <div className="flex justify-center pt-2 pb-1">
          <button onClick={onClearPick} className="text-sm" style={{ color: '#999' }}>
            נקה בחירה
          </button>
        </div>
      )}
    </div>
  );
};

export default ScorePickerSheet;
